import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { Lock, Link, Clock, ChevronRight, Calendar } from "lucide-react";
import { HEADING_COLOR } from "../../../constants/colors";
import ProgressJourney from "../../../components/ProgressJourney";
import DiscussWithMentor from "../../../components/mentors/DiscussWithMentor";
import { COLORS } from "../../../constants/colors";

const ExistingUserNoPathwayHome = () => {
  const navigate = useNavigate();
  const { firstName } = useSelector((state) => state.auth);
  const { currentJob, targetJob } = useSelector((state) => state.user);

  return (
    <div className="bg-white min-h-screen py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold px-2" style={{ color: HEADING_COLOR }}>
            Hi {firstName || "Alex"}, let's generate your personalized upskilling pathway
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base max-w-4xl font-medium mx-auto px-2">
            You've completed the essentials. Now create a tailored upskilling plan to AI proof your career.
          </p>
        </div>

        {/* Your Journey Section */}
        <ProgressJourney currentStep={5} totalSteps={6} />

        {/* Main Content - Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Left Column - Main Card Container */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-300 rounded-xl shadow-sm p-4 sm:p-6 md:p-7">
              {/* Main Heading */}
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Generate your personalized Upskilling Pathway</h2>
              
              {/* Two Sub-cards */}
              <div className="space-y-3 sm:space-y-4">
                {/* First Sub-card: Unlock your learning journey */}
                <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-purple-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">Unlock your learning journey</h3>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-500">
                          <Link className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">Clarify your target skills and learning journey</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-500">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">Get actionable steps to level up quickly</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Second Sub-card: Generate your Upskilling Pathway */}
                <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Generate your Upskilling Pathway</h3>
                      <p className="text-xs sm:text-sm text-gray-500 mb-2 leading-relaxed">
                        Create a customized plan to build the skills for your target role.
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="px-2 py-1 text-xs font-medium bg-white text-black border border-black rounded-full">
                          high
                        </span>
                        <span className="px-2 py-1 text-xs font-medium bg-white text-gray-600 border border-gray-400 rounded-full">
                          10 min
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/dashboard/skills-and-learning/upskilling-pathway-config")}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-3 text-white rounded-lg hover:opacity-90 transition-colors font-medium flex items-center justify-center sm:justify-start gap-2 text-sm"
                      style={{ backgroundColor: COLORS.purple }}
                    >
                      Start <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Use Existing DiscussWithMentor Component */}
          <div className="lg:col-span-1">
            <DiscussWithMentor 
              mentorName="Dr. Sarah Chen"
              mentorTitle="AI Career Expert"
              rating="4.9"
              reviews="500+"
              buttonText="Book Upskilling Pathway Session"
              buttonPath="/dashboard/mentors"
              showSecondButton={false}
              description="Not sure where to start? Our AI Career Genie can help guide you."
              icon={<Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />}
              className="h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExistingUserNoPathwayHome;
